import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CopySimpleIcon as CopySimple, CheckCircleIcon as CheckCircle, AtIcon as At, HashIcon as Hash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin / Social media bio library.
 *
 * Lives at /admin/social-bios. Holds curated bio copy for every
 * surface a founder might post on — IG, X/Twitter, LinkedIn,
 * TikTok, Facebook, YouTube About, Substack/Press one-liner,
 * and a long-form "About us" block.
 *
 * Why this page exists: the user asked us to keep these in the
 * admin console so they're easy to find when posting. Editing
 * happens here, in code — not user-typed — because bios are
 * carefully tuned legal/marketing copy and we don't want them
 * silently drifting between platforms.
 *
 * Conventions:
 *   • Each variant has a character limit annotated so it's
 *     obvious which one fits where (IG = 150, X = 160, etc).
 *   • Every variant respects the venue framing — never
 *     "registry," never "AKC," always "Armstrong K9 Registry."
 *   • Hashtag set is kept tight (3-5) per IG/X best practice.
 *   • Copy buttons auto-toast and revert after 2s.
 */

const SHORT_BIO_VARIANTS = [
  {
    id: "ig",
    platform: "Instagram",
    limit: 150,
    body: "Armstrong K9 Registry — the uncompromising standard for purebred & exotic dogs. Verified kennels. Audited pedigrees. Stripe-backed escrow. 🇺🇸",
  },
  {
    id: "x",
    platform: "X (Twitter)",
    limit: 160,
    body: "The trust-and-safety registry for purebred & exotic dogs. Verified kennels, audited pedigrees, Stripe-backed escrow, bookable trainers/groomers — one venue.",
  },
  {
    id: "tiktok",
    platform: "TikTok",
    limit: 80,
    body: "Verified kennels. Audited pedigrees. Escrow-protected. 🐕✨",
  },
  {
    id: "fb-short",
    platform: "Facebook (short)",
    limit: 101,
    body: "Verified kennels, audited pedigrees, escrow-backed sales, and vetted dog pros — all on one venue.",
  },
  {
    id: "yt-handle",
    platform: "YouTube tagline",
    limit: 70,
    body: "The uncompromising standard for purebred and exotic dogs.",
  },
];

const MEDIUM_BIO_VARIANTS = [
  {
    id: "linkedin",
    platform: "LinkedIn — Company",
    limit: 2000,
    body: `Armstrong K9 Registry is the uncompromising standard for purebred and exotic dogs. We're a trust-and-safety venue that connects vetted kennels, identity-verified buyers, USDA-licensed transporters, and insurance-verified service providers — trainers, groomers, walkers, sitters — on one platform.

Every dog sale runs through Stripe-backed escrow. Every pedigree is auditable six generations deep with COI inbreeding warnings flagged at registration. Every service booking goes through Stripe Connect with independent dispute resolution.

Armstrong K9 Registry is the venue. Kennels, providers, and transporters are independent businesses operating under their own insurance and licensing — we surface what's verified, we don't pretend to be the seller.

For breeders, buyers, transporters, service pros, storefronts, and the people who simply love their dogs.`,
  },
  {
    id: "press",
    platform: "Press / one-line",
    limit: 280,
    body: "Armstrong K9 Registry is an independent trust-and-safety venue for purebred and exotic dogs — combining audited pedigrees, escrow-backed sales, USDA-licensed transport, insurance-verified service providers, and direct-to-buyer kennel storefronts on one platform.",
  },
  {
    id: "substack",
    platform: "Substack / Newsletter",
    limit: 400,
    body: "Armstrong K9 Registry — the trust-and-safety registry for purebred and exotic dogs. We publish what 'verified' actually means: every kennel passes a seven-check audit, every pedigree is searchable six generations deep, and every transaction runs on Stripe-backed escrow. No registries named. No asterisks. Just the standard, written down.",
  },
  {
    id: "fb-long",
    platform: "Facebook (long)",
    limit: 700,
    body: "Armstrong K9 Registry is where serious dog people transact. We verify kennels through a seven-check audit, publish pedigrees six generations deep with COI inbreeding warnings, and route every dog sale through Stripe escrow that only releases when the dog physically arrives.\n\nBuyers also get one place to find vetted trainers, groomers, walkers, sitters, and USDA-licensed transporters — every provider runs on Stripe Connect with verified insurance and an independent dispute path.\n\nArmstrong K9 Registry is the venue. Kennels and providers are independent businesses operating under their own credentials.",
  },
];

const LONG_BIO_VARIANTS = [
  {
    id: "about",
    platform: "Website 'About' / Pitch Deck",
    limit: null,
    body: `Armstrong K9 Registry is the uncompromising standard for purebred and exotic dogs.

We exist because the dog economy runs on handshakes, screenshots, and Venmo — and the people who lose are the buyers, the responsible breeders, and the dogs. We don't tell breeders how to breed. We tell buyers what "verified" means here, in writing, with no asterisks.

What that means in practice:

• Kennels pass a seven-check verification audit before they get a gold WaxSeal. We walk away from kennels that won't pass.
• Pedigrees are auditable six generations deep, with COI inbreeding warnings flagged at registration — not after the litter is on the ground.
• Every dog sale runs through Stripe-backed escrow. Funds release only when the dog physically arrives, or after a 48-hour cool-down. Disputes freeze the escrow until Armstrong K9 Registry reviews.
• Transport is operated by independent, USDA-licensed haulers with $300K+ commercial-auto liability and their own Stripe Connect payouts.
• Trainers, groomers, walkers, sitters publish their own insurance and licensing on Stripe Connect — and run through the same escrow rails as the rest of the platform.

Armstrong K9 Registry is the venue. Kennels, providers, and transporters are independent businesses, not employees. You book and buy at your own risk under our at-own-risk venue terms — but you do so on a platform where every party's identity, insurance, and history is on the record.

One platform. Six audiences — breeders, buyers, transporters, service pros, storefronts, and dog owners. Each gets the tools they need without seeing the ones they don't.`,
  },
  {
    id: "deck-elevator",
    platform: "Pitch deck — elevator (90s)",
    limit: null,
    body: `Armstrong K9 Registry is the trust-and-safety venue for the purebred and exotic dog economy — a multi-billion-dollar market that still runs on handshakes and Venmo.

We turn it into Stripe-backed transactions. Kennels pass a seven-check audit before listing. Pedigrees are auditable six generations deep with COI warnings at registration. Dog sales run through Stripe escrow that releases only on arrival. Transport is USDA-licensed with $300K minimum liability. Trainers, groomers, walkers, sitters book through Stripe Connect with verified insurance.

Six revenue lines, one venue: registration fees, marketplace transaction fees, escrow fees, Stripe Connect rev-share on every service booking, $19/mo kennel storefronts, and a $25 intent-to-breed paywall on every breeding inquiry. Programmatic SEO at /puppies/{breed}/{state} compounds organic acquisition.

Armstrong K9 Registry — the uncompromising standard for purebred and exotic dogs.`,
  },
];

const HASHTAG_BUNDLES = [
  {
    id: "core",
    label: "Core brand (every post)",
    body: "#ArmstrongK9 #ArmstrongK9Registry",
  },
  {
    id: "marketplace",
    label: "Marketplace / buyer post",
    body: "#PurebredDogs #ExoticDogs #VerifiedKennel #PuppyForSale #ArmstrongK9Registry",
  },
  {
    id: "breeder",
    label: "Breeder / kennel post",
    body: "#ResponsibleBreeding #DogBreeder #PedigreeMatters #KennelLife #ArmstrongK9Registry",
  },
  {
    id: "pros",
    label: "Service-pro post (training/grooming/etc)",
    body: "#DogTrainer #DogGroomer #DogWalker #PetSitter #ArmstrongK9Registry",
  },
  {
    id: "owner",
    label: "Dog-owner / lifestyle post",
    body: "#DogLife #DogLog #DogParent #DogLovers #ArmstrongK9Registry",
  },
];

function BioCard({ variant, group }) {
  const [copied, setCopied] = useState(false);
  const len = variant.body.length;
  const overLimit = variant.limit && len > variant.limit;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(variant.body);
      setCopied(true);
      toast.success(`${variant.platform} bio copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };
  return (
    <div
      className="border border-[#E5E5E5] rounded-xl bg-white p-5 flex flex-col gap-3"
      data-testid={`social-bio-${group}-${variant.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">
            {variant.platform}
          </p>
          <p className={`text-[11px] mt-0.5 font-tabular ${overLimit ? "text-crimson font-bold" : "text-[#6B6B6B]"}`}>
            {len} {variant.limit ? `/ ${variant.limit}` : "chars"}{overLimit ? " — OVER LIMIT" : ""}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCopy}
          className="rounded-full border-ink/20 hover:border-gold hover:text-gold h-8 px-3 text-[12px]"
          data-testid={`social-bio-copy-${group}-${variant.id}`}
        >
          {copied ? <CheckCircle size={13} weight="fill" /> : <CopySimple size={13} />}
          <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-[13.5px] text-ink leading-relaxed bg-cream/40 border border-[#EFE9D6] rounded-lg p-3">
        {variant.body}
      </pre>
    </div>
  );
}

function HashtagRow({ bundle }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(bundle.body);
      setCopied(true);
      toast.success(`${bundle.label} hashtags copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };
  return (
    <div
      className="flex items-center justify-between gap-4 border border-[#E5E5E5] rounded-lg bg-white px-4 py-3"
      data-testid={`social-bio-hashtags-${bundle.id}`}
    >
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold">{bundle.label}</p>
        <p className="text-[13px] text-ink font-tabular truncate">{bundle.body}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleCopy}
        className="rounded-full border-ink/20 hover:border-gold hover:text-gold h-8 px-3 text-[12px] flex-shrink-0"
        data-testid={`social-bio-copy-hashtags-${bundle.id}`}
      >
        {copied ? <CheckCircle size={13} weight="fill" /> : <CopySimple size={13} />}
        <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
      </Button>
    </div>
  );
}

export default function AdminSocialBios() {
  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10" data-testid="admin-social-bios-page">
        <div className="mb-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">
            Admin · Marketing
          </p>
          <h1 className="font-spectral text-4xl md:text-5xl text-ink leading-tight">
            Social media bios
          </h1>
          <p className="mt-3 text-[15px] text-[#3F3F3F] max-w-2xl">
            Canonical bio copy for every surface — short (IG, X, TikTok), medium
            (LinkedIn, press one-liner), and long (About, pitch deck).
            One-tap copy. Edits live in code so the brand voice doesn't drift.
          </p>
        </div>

        {/* SHORT */}
        <section className="mb-12" data-testid="social-bios-short">
          <div className="flex items-center gap-2 mb-4">
            <At size={16} weight="bold" className="text-gold" />
            <h2 className="font-spectral text-2xl text-ink">Short bios</h2>
            <span className="text-[11px] text-[#6B6B6B] ml-1">Instagram, X, TikTok, Facebook, YouTube</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {SHORT_BIO_VARIANTS.map((v) => (
              <BioCard key={v.id} variant={v} group="short" />
            ))}
          </div>
        </section>

        {/* MEDIUM */}
        <section className="mb-12" data-testid="social-bios-medium">
          <div className="flex items-center gap-2 mb-4">
            <At size={16} weight="bold" className="text-gold" />
            <h2 className="font-spectral text-2xl text-ink">Medium bios</h2>
            <span className="text-[11px] text-[#6B6B6B] ml-1">LinkedIn, press, newsletter, Facebook long</span>
          </div>
          <div className="grid gap-4">
            {MEDIUM_BIO_VARIANTS.map((v) => (
              <BioCard key={v.id} variant={v} group="medium" />
            ))}
          </div>
        </section>

        {/* LONG */}
        <section className="mb-12" data-testid="social-bios-long">
          <div className="flex items-center gap-2 mb-4">
            <At size={16} weight="bold" className="text-gold" />
            <h2 className="font-spectral text-2xl text-ink">Long-form / About</h2>
            <span className="text-[11px] text-[#6B6B6B] ml-1">Website About page, pitch deck</span>
          </div>
          <div className="grid gap-4">
            {LONG_BIO_VARIANTS.map((v) => (
              <BioCard key={v.id} variant={v} group="long" />
            ))}
          </div>
        </section>

        {/* HASHTAG BUNDLES */}
        <section className="mb-12" data-testid="social-bios-hashtags">
          <div className="flex items-center gap-2 mb-4">
            <Hash size={16} weight="bold" className="text-gold" />
            <h2 className="font-spectral text-2xl text-ink">Hashtag bundles</h2>
            <span className="text-[11px] text-[#6B6B6B] ml-1">Tight, scoped to post type</span>
          </div>
          <div className="grid gap-3">
            {HASHTAG_BUNDLES.map((b) => (
              <HashtagRow key={b.id} bundle={b} />
            ))}
          </div>
        </section>

        <div className="mt-10 pt-8 border-t border-[#EAEAEA] text-[12px] text-[#6B6B6B] leading-relaxed max-w-3xl">
          <strong className="text-ink">Brand voice guardrails:</strong> Always
          "Armstrong K9 Registry" on first mention (never bare "Armstrong"
          on venue disclosures). Never name any other registry by name. Use
          "venue" language consistently — never "agency," "seller," or
          "marketplace operator." Keep the at-own-risk posture explicit in
          long-form copy.
        </div>
      </div>
    </AppShell>
  );
}
